import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import FormControlledDropdown from '../../../../components/inputs/FormInputs/FormControlledDropdown';
import FormControlledInput from '../../../../components/inputs/FormInputs/FormControlledInput';
import {
  fecthCostTypes,
  fecthJobs,
  fetchJCJobPhases,
  fetchPhaseCostTypes,
} from '../../../../services/accounting/JobsContractsService';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  APTransactionEntryForm,
  APTransactionItemType,
  UnitOfMeasure,
} from '../../../../Interfaces/Accounting/ACForms.interfaces';
import { JCPhase } from '../../../../Interfaces/Accounting/JobsContracts.interface';
import { useCompanyContext } from '../../../../context/CompanyContext';
import WrapperButton from '../../../../components/buttons/WrapperButton';
import FormControlledNumberInput from '@/src/app/components/inputs/FormInputs/FormControlledNumberInput';
import { fecthGLAccounts } from '@/src/app/services/accounting/GLAccountService';
import { formatCurrency } from '@/src/utils/formatUtils';
import { fetchUnitsOfMeasure } from '@/src/app/services/accounting/ACFormsService';

type APEntryItemProps = {
  fieldIndex: number;
  remove: (index: number) => void;
  fetch?: boolean;
};

const APEntryItem = ({ fieldIndex, remove, fetch }: APEntryItemProps) => {
  const { selectedCompany } = useCompanyContext();
  const { getValues, setValue } = useFormContext<APTransactionEntryForm>();
  const [onAmountFocus, setOnAmountFocus] = useState(false);
  const [pendingAmount, setPendingAmount] = useState<number>();
  const [type, job, phase, total, um] = useWatch<APTransactionEntryForm>({
    name: [
      `items.${fieldIndex}.type`,
      `items.${fieldIndex}.job`,
      `items.${fieldIndex}.phase`,
      `items.${fieldIndex}.total`,
      `items.${fieldIndex}.um`,
    ],
  });

  const jobs = useQuery({
    queryKey: ['getAPEntryJobs', selectedCompany],
    queryFn: ({ signal }) =>
      fecthJobs({ companyID: selectedCompany!.id, status: [1] }, signal),
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany && fetch,
  });

  const glItems = useQuery({
    queryKey: ['getAPEntryItems', selectedCompany],
    queryFn: ({ signal }) => {
      return fecthGLAccounts(selectedCompany!.id, [6, 7, 8, 9], signal);
    },
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany && fetch,
  });

  const phases = useQuery({
    queryKey: ['getJCJobPhases', selectedCompany, job],
    queryFn: ({ signal }) => {
      return fetchJCJobPhases(
        {
          companyID: selectedCompany!.id,
          job: job as string,
        },
        signal
      );
    },
    refetchOnWindowFocus: false,
    enabled: !!(selectedCompany && job),
  });

  const costTypes = useQuery({
    queryKey: ['getPhaseCostTypes', selectedCompany, job, phase],
    queryFn: ({ signal }) => {
      return fetchPhaseCostTypes(
        selectedCompany!.id,
        job as string,
        phase as string,
        signal
      );
    },
    refetchOnWindowFocus: false,
    enabled: !!(selectedCompany && job && phase),
  });

  const umRequest = useQuery({
    queryKey: ['getUnitOfMeasure'],
    queryFn: ({ signal }) => {
      return fetchUnitsOfMeasure(signal);
    },
    refetchOnWindowFocus: false,
    enabled: !!selectedCompany && fetch,
  });

  const calculatePending = (amount: number) => {
    const items = getValues('items');
    const total = getValues('total') ?? 0;

    const totalAmount = (items ?? []).reduce((acc, item, index) => {
      if (index === fieldIndex) {
        return acc;
      }

      return acc + (item.total ?? 0);
    }, 0);

    setPendingAmount(total - totalAmount - amount);
  };

  return (
    <div className="col-12 grid p-0">
      <div className="col-12 flex align-content-center gap-2">
        <label className="text-17px font-bold">Item {fieldIndex + 1}</label>
        <WrapperButton onClick={() => remove(fieldIndex)} className="h-fit">
          <i className="pi pi-trash  p-1 border-circle hover:bg-gray-300" />
        </WrapperButton>
      </div>
      <div className="col-12 sm:col-6">
        <label
          htmlFor={`items.${fieldIndex}.type`}
          className="text-standard block mb-2"
        >
          Entry Type
        </label>
        <FormControlledDropdown
          formID={`items.${fieldIndex}.type`}
          labelField="label"
          valueField="value"
          options={[
            { label: 'Job Cost', value: APTransactionItemType.jc },
            { label: 'GL', value: APTransactionItemType.gl },
          ]}
          placeholder={'Select type'}
          clearable={true}
          rules={{
            required: {
              value: true,
              message: 'Type is a required field',
            },
          }}
          isFieldArray={true}
          additionalChange={() => {
            setValue(`items.${fieldIndex}.glAcct`, null as unknown as string);
            setValue(`items.${fieldIndex}.job`, null as unknown as string);
            setValue(`items.${fieldIndex}.phase`, null as unknown as string);
            setValue(`items.${fieldIndex}.um`, null as unknown as string);
            setValue(`items.${fieldIndex}.costType`, null as unknown as number);
            setValue(`items.${fieldIndex}.units`, null as unknown as number);
            setValue(`items.${fieldIndex}.price`, null as unknown as number);
          }}
        />
      </div>
      {type === APTransactionItemType.jc && (
        <React.Fragment>
          <div className="col-12 sm:col-6" />
          <div className="col-12 sm:col-6">
            <label
              htmlFor={`items.${fieldIndex}.job`}
              className="text-standard block mb-2"
            >
              Job #
            </label>
            <FormControlledDropdown
              formID={`items.${fieldIndex}.job`}
              options={jobs.data}
              labelField="Job"
              valueField="Job"
              placeholder={jobs.isError ? 'Failed to load jobs!' : 'Select Job'}
              isDisabled={jobs.isError}
              isLoading={jobs.isFetching}
              clearable={true}
              filter={true}
              resetFilterOnHide={true}
              rules={{
                required: {
                  value: true,
                  message: 'Job is a required field',
                },
              }}
              isFieldArray={true}
              additionalChange={() => {
                setValue(
                  `items.${fieldIndex}.phase`,
                  null as unknown as string
                );
                setValue(
                  `items.${fieldIndex}.costType`,
                  null as unknown as number
                );
              }}
            />
          </div>
          <div className="col-12 sm:col-6">
            <label
              htmlFor={`items.${fieldIndex}.job`}
              className="text-standard block mb-2"
            >
              Job Name
            </label>
            <FormControlledDropdown
              formID={`items.${fieldIndex}.job`}
              options={jobs.data}
              labelField="Description"
              valueField="Job"
              placeholder={
                jobs.isError ? 'Failed to load job names!' : 'Select Job Name'
              }
              isDisabled={jobs.isError}
              isLoading={jobs.isFetching}
              clearable={true}
              filter={true}
              resetFilterOnHide={true}
              rules={{
                required: {
                  value: true,
                  message: 'Job Name is a required field',
                },
              }}
              isFieldArray={true}
              additionalChange={() => {
                setValue(
                  `items.${fieldIndex}.phase`,
                  null as unknown as string
                );
                setValue(
                  `items.${fieldIndex}.costType`,
                  null as unknown as number
                );
              }}
            />
          </div>
          <div className="col-12 sm:col-6">
            <label
              htmlFor={`items.${fieldIndex}.phase`}
              className="text-standard block mb-2"
            >
              Phase Code
            </label>
            <FormControlledDropdown
              formID={`items.${fieldIndex}.phase`}
              options={phases.data?.map((phase: JCPhase) => ({
                label: `${phase.Phase} - ${phase.Description}`,
                ...phase,
              }))}
              labelField="label"
              valueField="Phase"
              placeholder={
                phases.isError
                  ? 'Failed to load phase codes!'
                  : 'Select Phase Code'
              }
              isDisabled={phases.isError}
              isLoading={phases.isFetching}
              clearable={true}
              filter={true}
              resetFilterOnHide={true}
              rules={{
                required: {
                  value: true,
                  message: 'Phase Code is a required field',
                },
              }}
              isFieldArray={true}
              additionalChange={() => {
                setValue(
                  `items.${fieldIndex}.costType`,
                  null as unknown as number
                );
              }}
            />
          </div>
          <div className="col-12 sm:col-6">
            <label
              htmlFor={`items.${fieldIndex}.costType`}
              className="text-standard block mb-2"
            >
              Cost Type
            </label>
            <FormControlledDropdown
              formID={`items.${fieldIndex}.costType`}
              options={costTypes.data}
              labelField="description"
              valueField="costType"
              placeholder={
                costTypes.isError
                  ? 'Failed to load cost types!'
                  : 'Select Cost Type'
              }
              isDisabled={costTypes.isError}
              isLoading={costTypes.isFetching}
              clearable={true}
              filter={true}
              resetFilterOnHide={true}
              rules={{
                required: {
                  value: true,
                  message: 'Cost Type is a required field',
                },
              }}
              isFieldArray={true}
            />
          </div>
          <div className="col-12 sm:col-4">
            <label
              htmlFor={`items.${fieldIndex}.um`}
              className="text-standard block mb-2"
            >
              UoM
            </label>
            <FormControlledDropdown
              isFieldArray={true}
              formID={`items.${fieldIndex}.um`}
              options={umRequest.data?.filter(
                (um: UnitOfMeasure) => um.UM !== 'MOS' && um.UM !== 'QTR'
              )}
              labelField="Description"
              valueField="UM"
              placeholder={
                umRequest.isError ? 'Failed to load options' : 'Select Option'
              }
              isDisabled={umRequest.isError}
              isLoading={umRequest.isFetching}
              clearable={false}
              filter={true}
              resetFilterOnHide={true}
              rules={{
                required: {
                  value: true,
                  message: 'UoM is a required field',
                },
              }}
              ignoreOnBlur={true}
              additionalChange={(value) => {
                if (value === 'LS') {
                  setValue(`items.${fieldIndex}.units`, 0);
                  setValue(`items.${fieldIndex}.price`, 0);
                }
              }}
            />
          </div>

          <div className="col-12 sm:col-4">
            <label
              htmlFor={`items.${fieldIndex}.units`}
              className="text-standard block mb-2"
            >
              Units
            </label>
            <FormControlledNumberInput
              formID={`items.${fieldIndex}.units`}
              isFieldArray={true}
              min={0}
              disabled={um === 'LS'}
              rules={{
                required: {
                  value: true,
                  message: 'Units is a required field',
                },
              }}
              onChange={(value) => {
                setValue(
                  `items.${fieldIndex}.total`,
                  ((value as number) ?? 0) *
                    (getValues(`items.${fieldIndex}.price`) ?? 0)
                );
              }}
            />
          </div>
          <div className="col-12 sm:col-4">
            <label
              htmlFor={`items.${fieldIndex}.price`}
              className="text-standard block mb-2"
            >
              Price/ EA
            </label>
            <FormControlledNumberInput
              formID={`items.${fieldIndex}.price`}
              mode="currency"
              currency="USD"
              locale="en-US"
              disabled={um === 'LS'}
              isFieldArray={true}
              rules={{
                required: {
                  value: true,
                  message: 'Price/ EA is a required field',
                },
              }}
              onChange={(value) => {
                setValue(
                  `items.${fieldIndex}.total`,
                  ((value as number) ?? 0) *
                    (getValues(`items.${fieldIndex}.units`) ?? 0)
                );
              }}
            />
          </div>
        </React.Fragment>
      )}
      {type === APTransactionItemType.gl && (
        <div className="col-12 sm:col-6">
          <label
            htmlFor={`items.${fieldIndex}.glAcct`}
            className="text-standard block mb-2"
          >
            GL Account
          </label>
          <FormControlledDropdown
            formID={`items.${fieldIndex}.glAcct`}
            options={
              glItems.data?.map((data) => ({
                label: `${data.GLAcct} ${data.Description}`,
                value: data.GLAcct,
              })) ?? []
            }
            labelField="label"
            valueField="value"
            placeholder={
              glItems.isError ? 'Failed to load account!' : 'Select Account'
            }
            isDisabled={glItems.isError}
            isLoading={glItems.isFetching}
            clearable={true}
            filter={true}
            resetFilterOnHide={true}
            rules={{
              required: {
                value: true,
                message: 'GL Account is a required field',
              },
            }}
            isFieldArray={true}
          />
        </div>
      )}
      <div className="col-12 sm:col-6">
        <label htmlFor="description" className="text-standard block mb-2">
          Description
        </label>
        <FormControlledInput
          defaultValue=""
          formID={`items.${fieldIndex}.description`}
          rules={{
            required: {
              value: true,
              message: 'Description is a required field',
            },
          }}
          autocomplete="off"
          isFieldArray={true}
          maxLength={30}
        />
      </div>
      <div className="col-12 sm:col-6">
        <label htmlFor="total" className="text-standard block mb-2">
          Item Total
        </label>
        <FormControlledNumberInput
          formID={`items.${fieldIndex}.total`}
          disabled={type === APTransactionItemType.jc && um !== 'LS'}
          mode="currency"
          currency="USD"
          locale="en-US"
          rules={{
            required: {
              value: true,
              message: 'Total is a required field',
            },
          }}
          isFieldArray={true}
          onFocus={() => {
            setOnAmountFocus(true);
            calculatePending((total as number) ?? 0);
          }}
          onBlur={() => {
            setOnAmountFocus(false);
          }}
          onChange={(amount) => calculatePending(amount as number)}
        />
        {onAmountFocus && (
          <label className="ml-2 font-bold p-error">
            {!pendingAmount
              ? null
              : pendingAmount > 0
              ? `${formatCurrency(
                  pendingAmount
                )} remaining to reach invoice total`
              : `${formatCurrency(-1 * pendingAmount)} over the invoice total`}
          </label>
        )}
      </div>
    </div>
  );
};

export default APEntryItem;
